import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

export const COS_HANDOFF_SERVER_NAME = "cos_handoff" as const;
export const COS_HANDOFF_PROPOSE_TOOL_NAME = "propose_task_to_colleague";

export const COS_HANDOFF_TOOLS_METADATA = [
  {
    name: COS_HANDOFF_PROPOSE_TOOL_NAME,
    description:
      "Propose a task into a colleague's Home on the current user's behalf. The colleague sees who asked, why, and where it came from, and decides whether to accept it — nothing starts until they do. Use this to hand work to another person; do not use it to send them a message. To hand over a task the user already has, pass its `taskId`: it moves to the colleague with its due date and sources and leaves the user's list, rather than being copied.",
    schema: {
      recipientEmail: z
        .string()
        .describe(
          "Workspace email of the colleague the work is for. If you are unsure, call the tool with your best guess: the error lists every member."
        ),
      taskId: z
        .string()
        .optional()
        .describe(
          "The `id` of one of the user's own tasks, as list_my_tasks returned it, to hand over as it is. Omit it to propose new work."
        ),
      task: z
        .string()
        .optional()
        .describe(
          "New work you are asking them to do, in one line, phrased as work rather than as a question. Required without `taskId`; ignored with it."
        ),
      dueOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD")
        .refine(
          (value) =>
            !Number.isNaN(Date.parse(value)) &&
            new Date(value).toISOString().startsWith(value),
          "A real calendar date"
        )
        .optional()
        .describe(
          "When it is due, YYYY-MM-DD, if the request states one. With `taskId`, omit it to keep the task's own due date."
        ),
      rationale: z
        .string()
        .describe(
          "Why this is being asked of them now — the reason they will read before accepting."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Handing off",
      done: "Handed off",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
] as const;

export const COS_HANDOFF_SERVER = {
  serverInfo: {
    name: COS_HANDOFF_SERVER_NAME,
    version: "1.0.0",
    description:
      "Hand work from the current user to a colleague: a proposed task landing in their Home, attributed to the requester and the chief of staff that spoke for them, which they accept or decline.",
    authorization: null,
    icon: "ActionListCheckIcon",
    documentationUrl: null,
  },
  tools: COS_HANDOFF_TOOLS_METADATA,
} as const satisfies ServerMetadata;
